import { DocumentDownloadIcon } from "assets/icons";
import { If } from "components";
import { PAGE_SEARCH_PARAMETER } from "constants/common";
import { t } from "i18next";
import moment from "moment";
import { OrderModal } from "modules/orders";
import { useState } from "react";
import { toast } from "react-hot-toast";
import { useSearchParams } from "react-router-dom";
import {
  Button,
  FilterChips,
  InfoTooltipIcon,
  Pagination,
  SearchInput,
  TableFilter
} from "ui";

import {
  ProfileSummaryCards,
  ResellerCommissionsFilterForm,
  ResellerCommissionsTable
} from "../../components";
import {
  COMMISSION_FROM_FILTER_SEARCH_PARAMETER,
  COMMISSION_STATUS_FILTER_SEARCH_PARAMETER,
  COMMISSION_TO_FILTER_SEARCH_PARAMETER,
  ResellerCommissionSource
} from "../../constants";
import {
  exportResellerCommissionsToCsv,
  formatAmountInFcfa
} from "../../helpers";
import { useResellerCommissionsList } from "../../hooks";

import styles from "./ResellerCommissions.module.scss";

const ALL_SOURCES_VALUE = "All";

const ResellerCommissions = () => {
  const {
    resellerId,
    commissions,
    summary,
    shownTotal,
    totalItems,
    isLoading,
    page,
    setPage,
    searchValue,
    setSearchValue,
    sorting,
    setSorting,
    source,
    setSource,
    status,
    from,
    to,
    requestParams,
    refetch
  } = useResellerCommissionsList();

  const [searchParams, setSearchParams] = useSearchParams();
  const [openedOrderId, setOpenedOrderId] = useState<string | null>(null);
  const [isExporting, setIsExporting] = useState(false);

  const exportCommissions = async () => {
    setIsExporting(true);

    try {
      const { exportedCount, isTruncated } =
        await exportResellerCommissionsToCsv(
          resellerId,
          requestParams,
          `commissions-${resellerId}-${moment().format("YYYY-MM-DD")}.csv`
        );

      if (!exportedCount) {
        toast.error(t("There is nothing to export"));
      } else if (isTruncated) {
        toast.success(
          `${t("Only the first")} ${exportedCount} ${t("commissions were exported — narrow the period to get the rest")}`
        );
      } else {
        toast.success(`${exportedCount} ${t("commissions exported")}`);
      }
    } catch {
      toast.error(t("Export failed"));
    } finally {
      setIsExporting(false);
    }
  };

  const clearSearchParameter = (parameter: string | string[]) => {
    const parameters = Array.isArray(parameter) ? parameter : [parameter];
    parameters.forEach((key) => searchParams.delete(key));
    searchParams.delete(PAGE_SEARCH_PARAMETER);
    setSearchParams(searchParams);
  };

  const formatPeriod = () => {
    const fromLabel = from ? moment(from).format("DD/MM/YYYY") : "…";
    const toLabel = to ? moment(to).format("DD/MM/YYYY") : "…";

    return `${fromLabel} — ${toLabel}`;
  };

  return (
    <div className={styles.commissions}>
      {/* The tiles describe the whole eligible set whichever chip is active —
          that is deliberate on the backend, so they never jump while filtering. */}
      <ProfileSummaryCards
        isLoading={isLoading && !summary}
        cards={[
          {
            label: "Total commissions",
            value: formatAmountInFcfa(summary?.total),
            hint: `${summary?.eligibleOrdersCount ?? 0} ${t("eligible orders")}`,
            accent: "primary"
          },
          {
            label: "Via referrals",
            value: formatAmountInFcfa(summary?.viaReferral),
            hint: `${summary?.referralOrdersCount ?? 0} ${t("orders")}`
          },
          {
            label: "Via promo code",
            value: formatAmountInFcfa(summary?.viaPromoCode),
            hint: `${summary?.promoCodeOrdersCount ?? 0} ${t("orders")}`
          },
          {
            label: "Pending payout",
            value: formatAmountInFcfa(summary?.pendingPayout),
            hint: t("Payable at the next cycle"),
            accent: "warning"
          }
        ]}
      />

      <div className={styles.commissions_toolbar}>
        <FilterChips
          label="Commission source"
          value={source ?? ALL_SOURCES_VALUE}
          onChange={(nextValue) =>
            setSource(
              nextValue === ALL_SOURCES_VALUE
                ? null
                : (nextValue as ResellerCommissionSource)
            )
          }
          options={[
            {
              label: "All",
              value: ALL_SOURCES_VALUE,
              counter: summary?.eligibleOrdersCount ?? 0
            },
            {
              label: "Referees",
              value: ResellerCommissionSource.Referral,
              counter: summary?.referralOrdersCount ?? 0
            },
            {
              label: "Promo Code",
              value: ResellerCommissionSource.PromoCode,
              counter: summary?.promoCodeOrdersCount ?? 0
            }
          ]}
        />

        <div className={styles.commissions_actions}>
          <div className={styles.commissions_search}>
            <SearchInput
              value={searchValue}
              onChange={setSearchValue}
              placeholder={t("Order, customer, phone number")}
            />
          </div>

          <Button
            variant="default"
            icon={<DocumentDownloadIcon width={20} height={20} />}
            loading={isExporting}
            onClick={exportCommissions}
          >
            {t("Export CSV")}
          </Button>

          <TableFilter>
            {({ closeFilter }) => (
              <ResellerCommissionsFilterForm closeFilter={closeFilter} />
            )}
          </TableFilter>
        </div>
      </div>

      <If condition={!!status || !!from || !!to}>
        <div className={styles.commissions_activeFilters}>
          <span>{t("Active filters")}:</span>

          <If condition={!!status}>
            <span className={styles.commissions_activeFilter}>
              {t(String(status))}
              <span
                className={styles.commissions_activeFilter_clear}
                onClick={() =>
                  clearSearchParameter(COMMISSION_STATUS_FILTER_SEARCH_PARAMETER)
                }
              >
                ×
              </span>
            </span>
          </If>

          <If condition={!!from || !!to}>
            <span className={styles.commissions_activeFilter}>
              {formatPeriod()}
              <span
                className={styles.commissions_activeFilter_clear}
                onClick={() =>
                  clearSearchParameter([
                    COMMISSION_FROM_FILTER_SEARCH_PARAMETER,
                    COMMISSION_TO_FILTER_SEARCH_PARAMETER
                  ])
                }
              >
                ×
              </span>
            </span>
          </If>
        </div>
      </If>

      <div className={styles.commissions_table}>
        <ResellerCommissionsTable
          commissions={commissions ?? []}
          isLoading={isLoading}
          sorting={sorting}
          setSorting={setSorting}
          shownTotal={shownTotal}
          shownCount={totalItems}
          resellerId={resellerId}
          onRowClick={(commission) => setOpenedOrderId(commission.orderId)}
        />
      </div>

      <div className={styles.commissions_note}>
        <InfoTooltipIcon
          id="commissions-scope-tooltip"
          tooltipText={t(
            "No commission is paid on an order carrying a markdown, a bulk discount, a cross-sell offer, Spend & Save or a generic coupon. Referral commissions started being recorded on the day this shipped, so older orders are not listed."
          )}
        />
        {t("Why is an order missing from this list?")}
      </div>

      <Pagination
        currentPage={page}
        totalItems={totalItems}
        onChange={setPage}
      />

      <If condition={!!openedOrderId}>
        <OrderModal
          orderId={String(openedOrderId)}
          isOpen={!!openedOrderId}
          refetchOrders={refetch}
          onClose={() => setOpenedOrderId(null)}
        />
      </If>
    </div>
  );
};

export default ResellerCommissions;
